import React, { useEffect, useState } from 'react';
import {
  HStack,
  Stack,
  Wrap,
  WrapItem,
  NumberInput,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  NumberInputField,
  RadioGroup,
  Radio,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaTransgenderAlt, FaGenderless } from 'react-icons/fa';
import { GiMale, GiFemale } from 'react-icons/gi';
import { FiAlertCircle } from 'react-icons/fi';
import EditableTextBox from '../EditableTextBox.jsx';
import { useCharacter } from './Contexts/CharacterInfo.js';

const CharacterProfileInfo = ({ locked, imperial, missingName }) => {
  const {
    character,
    updateName,
    updateAge,
    updateGender,
    updateHeightM,
    updateHeightFt,
    updateHeightIn,
    updateWeightLbs,
    updateWeightKg,
    updateSpecies,
    updateUnsavedChanges,
  } = useCharacter();

  const isMobile = useBreakpointValue({ base: true, md: false });
  const [lock, setLock] = useState(locked);

  const blankValue = 'REDACTED';
  const textStyle1 = { fontSize: '23px', fontWeight: 'inherit' };
  const textStyle2 = { fontSize: '20px' };

  const genderDictionary = {
    1: <GiFemale strokeWidth={20} size={isMobile ? 20 : 25} />,
    2: <GiMale strokeWidth={20} size={isMobile ? 20 : 25} />,
    3: <FaTransgenderAlt size={isMobile ? 20 : 25} />,
    4: <FaGenderless size={isMobile ? 20 : 25} />,
    5: <FiAlertCircle strokeWidth={3} size={isMobile ? 20 : 25} />,
    6: (
      <Text fontSize={isMobile ? '14px' : '16px'} fontWeight={'bold'}>
        Other
      </Text>
    ),
  };

  const handleWeightChange = newWeight => {
    if (imperial) {
      updateWeightLbs(newWeight);
      updateWeightKg(Math.round(newWeight * 0.453));
    } else {
      updateWeightKg(newWeight);
      updateWeightLbs(Math.round(newWeight * 2.2));
    }
    updateUnsavedChanges(true);
  };

  const handleHeightChangeMetric = newHeight => {
    updateHeightM(newHeight);
    const totalInches = newHeight / 0.0254;
    updateHeightFt(Math.floor(totalInches / 12));
    updateHeightIn(Math.round(totalInches % 12));

    updateUnsavedChanges(true);
  };

  const handleHeightChangeImperial = (newHeight, isInch) => {
    var totalInches = 0;
    if (isInch) {
      updateHeightIn(newHeight);
      totalInches = character.heightFt * 12 + parseInt(newHeight);
    } else {
      updateHeightFt(newHeight);
      totalInches = newHeight * 12 + parseInt(character.heightIn);
    }
    updateHeightM(Math.round(totalInches * 0.254) / 10);
    updateUnsavedChanges(true);
  };

  useEffect(() => {
    setLock(locked);
  }, [locked]);

  useEffect(() => {}, [character]);

  return (
    <Wrap spacing="25px" justify="center" width={'75%'}>
      <WrapItem>
        <Stack
          verticalAlign={'center'}
          direction={{
            base: locked ? 'row' : 'column',
            md: 'row',
          }}
        >
          <b>Name</b>
          {lock ? (
            <div style={textStyle1}>{character.name || blankValue}</div>
          ) : (
            <EditableTextBox
              border={missingName ? '2px solid red' : undefined}
              words={character.name}
              boxWidth={isMobile ? '275px' : '300px'}
              onChange={newName => {
                updateName(newName);
                updateUnsavedChanges(true);
              }}
            />
          )}
        </Stack>
      </WrapItem>
      <WrapItem>
        <Stack
          verticalAlign={'center'}
          direction={{
            base: locked ? 'row' : 'column',
            md: 'row',
          }}
        >
          <b>Age</b>
          {lock ? (
            <div style={textStyle2}>{character.age}</div>
          ) : (
            <NumberInput
              min={0}
              max={999}
              width="100px"
              defaultValue={character.age}
              value={character.age}
              onChange={newAge => {
                updateAge(newAge);
                updateUnsavedChanges(true);
              }}
            >
              <NumberInputField border="2px solid black" height={'40px'} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          )}
        </Stack>
      </WrapItem>
      <WrapItem>
        <Stack
          direction={{
            base: locked ? 'row' : 'column',
            md: 'row',
          }}
        >
          <b>Gender</b>
          {lock ? (
            <div>{genderDictionary[character.gender]}</div>
          ) : (
            <RadioGroup
              onChange={newGender => {
                updateGender(newGender);
                updateUnsavedChanges(true);
              }}
              value={character.gender}
            >
              <HStack spacing={2}>
                <Radio value="1" spacing={isMobile ? 0 : 1}>
                  {genderDictionary[1]}
                </Radio>
                <Radio value="2" spacing={isMobile ? 0 : 1}>
                  {genderDictionary[2]}
                </Radio>
                <Radio value="3" spacing={isMobile ? 0 : 1}>
                  {genderDictionary[3]}
                </Radio>
                <Radio value="4" spacing={isMobile ? 0 : 1}>
                  {genderDictionary[4]}
                </Radio>
                <Radio value="5" spacing={isMobile ? 0 : 1}>
                  {genderDictionary[5]}
                </Radio>
                <Radio value="6" spacing={isMobile ? 0 : 1}>
                  {genderDictionary[6]}
                </Radio>
              </HStack>
            </RadioGroup>
          )}
        </Stack>
      </WrapItem>
      <WrapItem>
        <HStack>
          <b>Height</b>
          {imperial ? (
            <HStack>
              {lock ? (
                <div style={textStyle2}>{character.heightFt}</div>
              ) : (
                <NumberInput
                  min={0}
                  max={20}
                  width="75px"
                  defaultValue={character.heightFt}
                  value={character.heightFt}
                  onChange={newHeight => {
                    handleHeightChangeImperial(newHeight, false);
                  }}
                >
                  <NumberInputField border="2px solid black" height={'40px'} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              )}
              <div>ft</div>
              {lock ? (
                <div style={textStyle2}>{character.heightIn}</div>
              ) : (
                <NumberInput
                  min={0}
                  max={11}
                  width="75px"
                  defaultValue={character.heightIn}
                  value={character.heightIn}
                  onChange={newHeight => {
                    handleHeightChangeImperial(newHeight, true);
                  }}
                >
                  <NumberInputField border="2px solid black" height={'40px'} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              )}
              <div>in</div>
            </HStack>
          ) : (
            <HStack>
              {lock ? (
                <div style={textStyle2}>{character.heightM}</div>
              ) : (
                <NumberInput
                  min={0}
                  max={6.4}
                  precision={1}
                  step={0.1}
                  width="75px"
                  defaultValue={character.heightM}
                  value={character.heightM}
                  onChange={newHeight => {
                    handleHeightChangeMetric(newHeight);
                  }}
                >
                  <NumberInputField border="2px solid black" height={'40px'} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              )}
              <div>m</div>
            </HStack>
          )}
        </HStack>
      </WrapItem>
      <WrapItem>
        <HStack>
          <b>Weight</b>
          {lock ? (
            <div style={textStyle2}>
              {imperial ? character.weightLbs : character.weightKg}
            </div>
          ) : (
            <NumberInput
              min={0}
              max={imperial ? 999 : 450}
              width="85px"
              defaultValue={imperial ? character.weightLbs : character.weightKg}
              onChange={newWeight => handleWeightChange(newWeight)}
              value={imperial ? character.weightLbs : character.weightKg}
            >
              <NumberInputField border="2px solid black" height={'40px'} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          )}
          {imperial ? <div>lbs</div> : <div>kgs</div>}
        </HStack>
      </WrapItem>
      <WrapItem>
        <Stack
          direction={{
            base: locked ? 'row' : 'column',
            md: 'row',
          }}
        >
          <b>Species</b>
          {lock ? (
            <div style={textStyle2}>{character.species || blankValue}</div>
          ) : (
            <EditableTextBox
              words={character.species}
              boxWidth={'250px'}
              onChange={newSpecies => {
                updateSpecies(newSpecies);
                updateUnsavedChanges(true);
              }}
            />
          )}
        </Stack>
      </WrapItem>
    </Wrap>
  );
};

export default CharacterProfileInfo;
